// sapi-admin/src/Pages/OperatorAccounts.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, doc, addDoc, 
  onSnapshot, serverTimestamp, query, orderBy, limit, runTransaction, 
  where
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { motion } from 'framer-motion';
import { 
  DollarSign, Plus, History, RefreshCw, Wallet, AlertTriangle 
} from 'lucide-react';
import { toast } from 'react-toastify';

export default function OperatorAccounts() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState({ orange: null, mtn: null });
  const [recharges, setRecharges] = useState([]);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState('orange');
  const [rechargeAmount, setRechargeAmount] = useState(10000);
  const [rechargeNote, setRechargeNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState([]);

  // ─── Listen to operator accounts ──────────────────────────────────────
  useEffect(() => {
    const unsubs = [];

    const unsubscribeOrange = onSnapshot(
      doc(db, 'operatorAccounts', 'orange'),
      (snap) => {
        if (snap.exists()) {
          setAccounts(prev => ({ ...prev, orange: { id: 'orange', ...snap.data() } }));
        } else {
          // Create document if it doesn't exist
          setAccounts(prev => ({ ...prev, orange: { id: 'orange', balance: 0, reservedBalance: 0, totalRecharged: 0, totalSpent: 0 } }));
        }
        setLoading(false);
      }
    );
    unsubs.push(unsubscribeOrange);

    const unsubscribeMtn = onSnapshot(
      doc(db, 'operatorAccounts', 'mtn'),
      (snap) => {
        if (snap.exists()) {
          setAccounts(prev => ({ ...prev, mtn: { id: 'mtn', ...snap.data() } }));
        } else {
          setAccounts(prev => ({ ...prev, mtn: { id: 'mtn', balance: 0, reservedBalance: 0, totalRecharged: 0, totalSpent: 0 } }));
        }
      }
    );
    unsubs.push(unsubscribeMtn);

    // Recharge history
    const q = query(collection(db, 'operatorRecharges'), orderBy('timestamp', 'desc'), limit(100));
    const unsubscribeRecharges = onSnapshot(q, (snap) => {
      setRecharges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubs.push(unsubscribeRecharges);

    // Pending transactions (status 'processing')
    const pendingQuery = query(collection(db, 'piSellTransactions'), where('status', '==', 'processing'));
    const unsubscribePending = onSnapshot(pendingQuery, (snap) => {
      setPendingTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubs.push(unsubscribePending);

    return () => unsubs.forEach(u => u());
  }, []);

  // ─── Recharge an account ──────────────────────────────────────────────
  const handleRecharge = async () => {
    if (rechargeAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);
    try {
      const accountRef = doc(db, 'operatorAccounts', selectedOperator);
      
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(accountRef);
        if (!snap.exists()) {
          // Create document if it doesn't exist
          transaction.set(accountRef, {
            balance: rechargeAmount,
            reservedBalance: 0,
            totalRecharged: rechargeAmount,
            totalSpent: 0,
            updatedAt: serverTimestamp(),
            lastRechargedAt: serverTimestamp(),
            rechargeCount: 1
          });
        } else {
          const data = snap.data();
          transaction.update(accountRef, {
            balance: (data.balance || 0) + rechargeAmount,
            totalRecharged: (data.totalRecharged || 0) + rechargeAmount,
            lastRechargedAt: serverTimestamp(),
            rechargeCount: (data.rechargeCount || 0) + 1,
            updatedAt: serverTimestamp()
          });
        }
      });

      // Add to recharge history
      const user = auth.currentUser;
      await addDoc(collection(db, 'operatorRecharges'), {
        operator: selectedOperator,
        amount: rechargeAmount,
        adminId: user?.uid || 'admin',
        adminName: user?.displayName || 'Admin',
        timestamp: serverTimestamp(),
        note: rechargeNote || 'Manual recharge'
      });

      toast.success(`✅ ${selectedOperator.toUpperCase()} recharged with ${rechargeAmount.toLocaleString()} FCFA`);
      setShowRechargeModal(false);
      setRechargeNote('');
      setRechargeAmount(10000);

    } catch (error) {
      console.error('Recharge error:', error);
      toast.error('Recharge failed: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate?.() || new Date(timestamp);
    return date.toLocaleString('en-US', { 
      day: '2-digit', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });
  };

  const getOperatorIcon = (op) => op === 'orange' ? '🍊' : '📡';
  const getOperatorColor = (op) => op === 'orange' ? 'orange' : 'cyan';
  const getOperatorBg = (op) => op === 'orange' ? 'bg-orange-500/10' : 'bg-cyan-500/10';
  const getBorderColor = (op) => op === 'orange' ? 'border-orange-500/30' : 'border-cyan-500/30';

  if (loading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(19, 160, 0, 0.69)', borderTopColor: '#00ff0d', animation: 'spin .8s linear infinite' }} />  
      <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading Operator Accounts…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  }

  const totalBalance = (accounts.orange?.balance || 0) + (accounts.mtn?.balance || 0);
  const totalReserved = (accounts.orange?.reservedBalance || 0) + (accounts.mtn?.reservedBalance || 0);
  const totalAvailable = totalBalance - totalReserved;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Wallet size={24} className="text-amber-400" />
            Operator Accounts
          </h1>
          <p className="text-sm text-slate-400 mt-1">Manage Orange Money and MTN Mobile Money accounts</p>
        </div>
        <button onClick={() => window.location.reload()} className="p-2 bg-white/5 rounded-xl hover:bg-white/10 transition">
          <RefreshCw size={18} className="text-slate-400" />
        </button>
      </div>

      {/* ─── Account Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {['orange', 'mtn'].map((op) => {
          const account = accounts[op];
          const label = op === 'orange' ? 'Orange Money' : 'MTN Mobile Money';
          const color = getOperatorColor(op);
          const bg = getOperatorBg(op);
          const border = getBorderColor(op);
          const textColor = op === 'orange' ? 'text-orange-400' : 'text-cyan-400';

          return (
            <motion.div
              key={op}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: op === 'orange' ? 0 : 0.1 }}
              className={`rounded-2xl bg-[#111827]/80 backdrop-blur-xl border ${border} p-6 shadow-xl`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center text-xl`}>
                    {getOperatorIcon(op)}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{label}</h3>
                    <p className="text-xs text-slate-500">Available balance</p>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedOperator(op); setShowRechargeModal(true); }}
                  className={`px-3 py-1.5 rounded-lg ${bg} ${textColor} text-xs font-medium flex items-center gap-1 hover:opacity-80 transition`}
                >
                  <Plus size={14} /> Recharge
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-3xl font-bold text-white">
                      {account?.balance?.toLocaleString() || 0} <span className="text-sm font-normal text-slate-400">FCFA</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Reserved: <span className="text-amber-400">{account?.reservedBalance?.toLocaleString() || 0} FCFA</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Total recharged</p>
                    <p className="text-sm font-semibold text-emerald-400">{account?.totalRecharged?.toLocaleString() || 0} FCFA</p>
                  </div>
                </div>

                <div className="flex justify-between text-xs text-slate-500 border-t border-white/5 pt-3">
                  <span>Last recharge: {formatDate(account?.lastRechargedAt)}</span>
                  <span>Recharges: {account?.rechargeCount || 0}</span>
                </div>

                {/* Balance progress bar */}
                <div className="w-full h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${op === 'orange' ? 'bg-orange-400' : 'bg-cyan-400'}`}
                    style={{ width: account?.balance > 0 ? `${Math.min((account.balance / 1000000) * 100, 100)}%` : '0%' }}
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ─── Quick Stats ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#111827]/80 rounded-xl p-4 border border-white/5">
          <p className="text-xs text-slate-500">Total Balance</p>
          <p className="text-lg font-bold text-white">{totalBalance.toLocaleString()} FCFA</p>
        </div>
        <div className="bg-[#111827]/80 rounded-xl p-4 border border-white/5">
          <p className="text-xs text-slate-500">Reserved</p>
          <p className="text-lg font-bold text-amber-400">{totalReserved.toLocaleString()} FCFA</p>
        </div>
        <div className="bg-[#111827]/80 rounded-xl p-4 border border-white/5">
          <p className="text-xs text-slate-500">Available</p>
          <p className="text-lg font-bold text-emerald-400">{totalAvailable.toLocaleString()} FCFA</p>
        </div>
        <div className="bg-[#111827]/80 rounded-xl p-4 border border-white/5">
          <p className="text-xs text-slate-500">Pending Transactions</p>
          <p className="text-lg font-bold text-amber-400">{pendingTransactions.length}</p>
        </div>
      </div>

      {/* ─── Pending Transactions List ─────────────────────────────────── */}
      {pendingTransactions.length > 0 && (
        <div className="bg-[#111827]/80 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold mb-3">
            <AlertTriangle size={16} /> Pending Transactions ({pendingTransactions.length})
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {pendingTransactions.map(tx => (
              <div key={tx.id} className="flex justify-between items-center text-xs bg-white/5 p-2 rounded-lg">
                <span>#{tx.id.slice(0,8)} - {tx.phoneNumber} ({tx.operator})</span>
                <span className="text-amber-400">{tx.fcfaAmount.toLocaleString()} FCFA</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Recharge History ───────────────────────────────────────────── */}
      <div className="bg-[#111827]/80 backdrop-blur-xl rounded-2xl border border-white/5 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <History size={18} className="text-slate-400" />
            <h3 className="text-white font-semibold">Recharge History</h3>
          </div>
          <span className="text-xs text-slate-500">{recharges.length} entries</span>
        </div>

        {recharges.length === 0 ? (
          <div className="text-center text-slate-500 py-8 text-sm">No recharge history</div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {recharges.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{r.operator === 'orange' ? '🍊' : '📡'}</span>
                  <div>
                    <p className="text-sm text-white font-medium">{r.operator.toUpperCase()} - {r.note || 'Recharge'}</p>
                    <p className="text-xs text-slate-500">{formatDate(r.timestamp)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-400">+{r.amount.toLocaleString()} FCFA</p>
                  <p className="text-xs text-slate-500">by {r.adminName || 'Admin'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Recharge Modal ─────────────────────────────────────────────── */}
      {showRechargeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#111827] border border-white/10 rounded-2xl max-w-md w-full p-6"
          >
            <h3 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
              <DollarSign size={20} className="text-amber-400" />
              Recharge {selectedOperator.toUpperCase()}
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Add funds to the {selectedOperator === 'orange' ? 'Orange Money' : 'MTN Mobile Money'} account for payouts.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Amount (FCFA)</label>
                <div className="flex gap-2 flex-wrap">
                  {[5000, 10000, 25000, 50000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setRechargeAmount(amount)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition ${
                        rechargeAmount === amount
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-white/5 text-slate-300 hover:bg-white/10'
                      }`}
                    >
                      {amount.toLocaleString()}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(Number(e.target.value) || 0)}
                  className="w-full mt-2 bg-slate-800/50 border border-white/10 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="text-xs text-slate-400 block mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={rechargeNote}
                  onChange={(e) => setRechargeNote(e.target.value)}
                  className="w-full bg-slate-800/50 border border-white/10 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50"
                  placeholder="Note"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowRechargeModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-slate-300 hover:bg-white/10 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecharge}
                  disabled={isSubmitting || rechargeAmount <= 0}
                  className={`flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium hover:opacity-90 transition disabled:opacity-50`}
                >
                  {isSubmitting ? 'Processing...' : `Recharge ${rechargeAmount.toLocaleString()} FCFA`}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}